import './SidebarMenu.scss';

import { FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { Search } from './Search';
import { SelectInfo } from 'rc-menu/es/interface';
import { useLocation } from 'react-router-dom';

import {
	applicationManagementSubMenu,
	dashboardMenuItem,
	dataSecuritySubMenu,
	governanceSubMenu,
	impactAnalysisSubMenu,
	logsSubMenu,
	useTagSubMenu
} from './menuitems';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SonarMenu, SonarMenuItem } from '@ui/menu';
import { useApplicationSidebar, usePrevious } from '@hooks';

function buildSelectInfoLookup(items: Array<SonarMenuItem>, parentKeys?: Array<string>): Record<string, Array<string>> {
	let result: Record<string, Array<string>> = {};

	for (let i = 0; i < items.length; i++) {
		const entry = items[ i ];
		const entryKey = entry.key as string;

		const pathKeys = [ entryKey, ...parentKeys ?? [] ];

		result[ entryKey ] = pathKeys;

		if (entry.children?.length) {
			const nestedResult = buildSelectInfoLookup(entry.children, pathKeys);

			result = {
				...result,
				...nestedResult
			};
		}
	}

	return result;
}

export const SidebarMenu: React.FC = () => {
	const location = useLocation();
	const tagsSubmenu = useTagSubMenu();
	const { isClosed, toggle } = useApplicationSidebar();

	const isClosedPrevious = usePrevious(isClosed, isClosed);

	const selectInfoLookupRef = useRef<Record<string, Array<string>>>();
	const currentOpenKeysRef = useRef([ impactAnalysisSubMenu.key as string ]);
	const openKeysCacheRef = useRef(currentOpenKeysRef.current);

	const [ selected, setSelected ] = useState([ '-1' ]);
	const [ openKeys, setOpenKeys ] = useState(currentOpenKeysRef.current);

	const handleTitleClick = useCallback((args: { key: string }) => {
		if (!isClosed) {
			return;
		}

		if (!currentOpenKeysRef.current.includes(args.key)) {
			currentOpenKeysRef.current.push(args.key);
		}

		toggle();
	}, [ isClosed, toggle ]);

	const calculatedMenuItems = useMemo(() => {
		const items = [
			dashboardMenuItem,
			{
				...impactAnalysisSubMenu,
				children: isClosed ? impactAnalysisSubMenu.children ?? [] : [
					{
						className: 'sidebar-search-entry',
						key: 'sidebar-search',
						label: <Search />
					},
					...impactAnalysisSubMenu.children ?? []
				],
				onTitleClick: handleTitleClick
			},
			{
				...governanceSubMenu,
				children: [
					...governanceSubMenu.children ?? [],
					tagsSubmenu
				],
				onTitleClick: handleTitleClick
			},
			{
				...dataSecuritySubMenu,
				onTitleClick: handleTitleClick
			},
			{
				...applicationManagementSubMenu,
				onTitleClick: handleTitleClick
			},
			{
				...logsSubMenu,
				onTitleClick: handleTitleClick
			}
		];

		selectInfoLookupRef.current = buildSelectInfoLookup(items);

		return items;
	}, [ handleTitleClick, isClosed, tagsSubmenu ]);

	const handleOpenChange = useCallback((keys: Array<string>) => {
		if (!isClosed) {
			currentOpenKeysRef.current = keys;
		}

		setOpenKeys(keys);
	}, [ isClosed ]);

	const handleSelectionChange = useCallback((info: SelectInfo) => {
		if (info.keyPath.length > 1) {
			const parentKeys = info.keyPath.slice(1);
			const sourceKeys = isClosed ? openKeysCacheRef.current : currentOpenKeysRef.current;

			const toOpenKeys = parentKeys.filter((key) => !sourceKeys.includes(key));

			if (info.selectedKeys.includes(FULL_ROUTES.TAGS_MANAGE) && !sourceKeys.includes(FULL_ROUTES.TAGS_MANAGE)) {
				toOpenKeys.push(FULL_ROUTES.TAGS_MANAGE);
			}

			if (toOpenKeys.length) {
				if (isClosed) {
					openKeysCacheRef.current = [ ...sourceKeys, ...toOpenKeys ];
				}

				if (!isClosed) {
					handleOpenChange([ ...currentOpenKeysRef.current, ...toOpenKeys ]);
					setOpenKeys(currentOpenKeysRef.current);
				}
			}
		}

		setSelected(info.selectedKeys);
	}, [ handleOpenChange, isClosed ]);

	useEffect(() => {
		const pathKey = location.pathname;

		if (!selected.includes(pathKey)) {
			handleSelectionChange({
				key: pathKey,
				keyPath: (selectInfoLookupRef.current ?? {})[ pathKey ] ?? [],
				selectedKeys: [ pathKey ]
			} as SelectInfo);
		}
	}, [ handleSelectionChange, location.pathname, selected ]);

	useEffect(() => {
		if (isClosed && !isClosedPrevious) {
			openKeysCacheRef.current = currentOpenKeysRef.current;
		}

		if (!isClosed && isClosedPrevious) {
			handleOpenChange(openKeysCacheRef.current);
		}

		if (!isClosed && !currentOpenKeysRef.current.length) {
			handleOpenChange([ impactAnalysisSubMenu.key as string ]);
		}
	}, [ handleOpenChange, isClosed, isClosedPrevious ]);

	return <SonarMenu
		id='main-menu'
		items={ calculatedMenuItems }
		mode='inline'
		onOpenChange={ handleOpenChange }
		onSelect={ handleSelectionChange }
		openKeys={ openKeys }
		selectedKeys={ selected }
		theme='dark' />;
};
